import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { PALETTE, FONT_BODY, FONT_DISPLAY, FONT_MONO } from "@/components/cobrain/tokens";
import { openCallMeNow } from "@/lib/openCallMeNow";
import { OutcomeStrip } from "@/components/cobrain/OutcomeStrip";
import { PartnerMarquee } from "@/components/cobrain/PartnerMarquee";
import { TestimonialWall } from "@/components/cobrain/TestimonialWall";
import { useReveal } from "@/hooks/useReveal";
import { SectionRail } from "@/components/landing/SectionRail";
import { AuditSample } from "@/components/services/AuditSample";
import { CompareMatrix } from "@/components/services/CompareMatrix";
import { AuditOutlineForm } from "@/components/services/AuditOutlineForm";
import { PaybackEstimator } from "@/components/services/PaybackEstimator";
import { ServiceTimeline } from "@/components/services/ServiceTimeline";
import { EngagementSnapshots } from "@/components/services/EngagementSnapshots";
import { VerticalStrip } from "@/components/services/VerticalStrip";
import { ExitIntent } from "@/components/landing/ExitIntent";
import { HeroAmbient, HeroTicker, AvailabilityPill } from "@/components/services/HeroAmbient";
import { LaneChooser, LANES, type LaneId } from "@/components/services/LaneChooser";
import { FitCheck } from "@/components/services/FitCheck";
import { AfterClick } from "@/components/services/AfterClick";
import { OutlineModal } from "@/components/services/OutlineModal";
import { EngineerCard } from "@/components/services/EngineerCard";
import { useServicesLane } from "@/hooks/useServicesLane";
import { useStickyCtaVisible } from "@/hooks/useStickyCtaVisible";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";

const RAIL_SECTIONS = [
  { id: "what", n: "01", label: "Three lanes" },
  { id: "payback", n: "02", label: "Your payback" },
  { id: "compare", n: "03", label: "Versus" },
  { id: "how", n: "04", label: "How it runs" },
  { id: "verticals", n: "05", label: "Your industry" },
  { id: "fit", n: "06", label: "Is it for you" },
  { id: "proof", n: "07", label: "Proof" },
  { id: "team", n: "08", label: "Who shows up" },
  { id: "faq", n: "09", label: "FAQ" },
];

// In-page anchors shown in the shared nav's secondary row + mobile sheet.
const NAV_SECTIONS = RAIL_SECTIONS.map((s) => ({ href: `#${s.id}`, label: s.label }));

/* ============================================================
   SERVICES — the "done with you" lane.
   How a deployment works: audit, build, deploy, run.
   Starting price comes from src/lib/offer.ts; exact scope set on the call.
   ============================================================ */

const CTA_LABEL = "Talk to Us Now";

function CallButton({ source, variant = "solid", label = CTA_LABEL }: {
  source: string; variant?: "solid" | "ghost"; label?: string;
}) {
  const solid = variant === "solid";
  return (
    <button
      type="button"
      onClick={() => openCallMeNow(source)}
      data-svc-cta={variant}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
        minHeight: 48, padding: "12px 22px",
        background: solid ? PALETTE.amber : "transparent",
        color: solid ? "#fff" : PALETTE.ink,
        border: solid ? 0 : `1px solid ${PALETTE.ink}`,
        borderRadius: 999, fontSize: 15, fontWeight: 600, cursor: "pointer",
        fontFamily: FONT_BODY,
        boxShadow: solid ? "0 12px 32px -10px rgba(196,130,58,0.55)" : "none",
        transition: "background 200ms ease, transform 200ms ease",
      }}
    >
      {label} <span aria-hidden>→</span>
    </button>
  );
}

function Reveal({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useReveal<HTMLDivElement>();
  return (
    <div ref={ref} data-reveal style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function StickyCallBar({ label }: { label?: string }) {
  // Same rules as the homepage pill: mobile only, after the hero, and never
  // stacked on top of another visible CTA.
  const { isMobile, visible } = useStickyCtaVisible({
    selectors: ["[data-svc-hero-cta]", ".svc-close", ".svc-lane-row", "footer"],
    minScroll: 640,
    footerGap: 240,
    threshold: 0.15,
  });

  // Lift the Intercom launcher while the pill is on screen so the bubble never
  // covers the button; restore the default padding on unmount.
  useEffect(() => {
    const w = window as any;
    const pad = visible ? 92 : 20;
    const apply = () => {
      if (typeof w.Intercom !== "function") return false;
      try { w.Intercom("update", { vertical_padding: pad, horizontal_padding: 20 }); } catch { /* noop */ }
      return true;
    };
    if (!apply()) {
      const id = window.setInterval(() => { if (apply()) window.clearInterval(id); }, 400);
      window.setTimeout(() => window.clearInterval(id), 8000);
      return () => window.clearInterval(id);
    }
  }, [visible]);

  useEffect(() => () => {
    try { (window as any).Intercom?.("update", { vertical_padding: 20, horizontal_padding: 20 }); } catch { /* noop */ }
  }, []);

  if (!isMobile) return null;

  return (
    <div
      className="svc-sticky-bar"
      aria-hidden={!visible}
      style={{
        position: "fixed", left: 12, right: 76, zIndex: 45,
        bottom: "max(16px, env(safe-area-inset-bottom))",
        padding: "9px 10px 9px 16px",
        borderRadius: 999,
        background: "rgba(248,244,236,0.94)",
        backdropFilter: "blur(14px) saturate(140%)",
        WebkitBackdropFilter: "blur(14px) saturate(140%)",
        border: `1px solid ${PALETTE.line}`,
        boxShadow: "0 12px 32px -10px rgba(0,0,0,0.22)",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(20px)",
        pointerEvents: visible ? "auto" : "none",
        transition: "opacity 260ms ease, transform 260ms cubic-bezier(.2,.8,.2,1)",
      }}
    >
      <div style={{ minWidth: 0, flex: "1 1 auto", lineHeight: 1.15 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: PALETTE.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label ?? "Free 15-min call"}</div>
        <div style={{ fontSize: 11, color: PALETTE.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Engineer, not a salesperson</div>
      </div>
      <span style={{ flex: "0 0 auto" }}>
        <CallButton source="services_sticky" label="Call" />
      </span>
    </div>
  );
}

const SERVICES = [
  {
    n: "01",
    name: "AI Readiness Audit",
    tag: "Start here",
    body: "An engineer sits down with your business, maps how work actually moves, and shows you exactly where AI pays off — and where it doesn't.",
    bullets: [
      "A map of how work and tools move through your operation",
      "A written plan, ranked by what pays back soonest",
      "Days, not a discovery phase",
    ],
  },
  {
    n: "02",
    name: "Build & Deploy",
    tag: "Done for you",
    body: "We implement the plan. Eva, CoBrain, integrations, custom workflows — whatever your business actually needs, wired into the tools you already run.",
    bullets: [
      "Workflows built around your operation, not a template",
      "Wired into the CRM, inbox, calendar and books you already run",
      "Iterated every month, not shipped and forgotten",
    ],
  },
  {
    n: "03",
    name: "Train or Staff",
    tag: "Done with you",
    body: "We train your existing ops person to run AI in-house — or we staff a trained operator into the business until you're ready to take it over.",
    bullets: [
      "Hands-on training for your team, on your own workflows",
      "A trained operator in the seat when you need headcount",
      "Someone accountable for it Monday morning",
    ],
  },
];

const RISK = [
  { t: "We'll talk you out of it", b: "If the payback isn't there, you hear it on the first call — before you've spent anything." },
  { t: "The plan is yours", b: "Once the audit is paid for, what it produces is yours. Hire us, hire someone else, or run it in-house." },
  { t: "Short first term", b: "A 3-month first term, then month to month. Cancel with 30 days' notice when the work is done." },
  { t: "Handover on request", b: "We train your people as we go, so taking it in-house is a decision, not a migration." },
];

const FAQS = [
  { q: "How much does this cost?", a: "Engagements start at $1,500/mo with a 3-month initial term, scoped to your size and what we build. You get an exact number on the call — and if the payback isn't obvious, we'll tell you not to spend it." },
  { q: "How long until we see something working?", a: "The audit lands within days of the call, and the first workflow is usually live inside a week. Nothing here runs on a quarterly cadence." },
  { q: "What do you need from us?", a: "About an hour a week from someone who knows how the work actually moves, plus access to the tools we're wiring into. That's the whole ask." },
  { q: "What happens if we stop?", a: "Once your engagement is paid in full, you own the deliverables we built for you, including the plan and documentation. Our underlying platform stays ours. Your people are trained as we go, so leaving is a decision rather than a project." },
  { q: "What is this not a fit for?", a: "Not a six-figure transformation program, not a twelve-month roadmap, and not a reseller of somebody else's AI stack. If you need enterprise change management, we're the wrong shop." },
  { q: "Do I have to buy your software?", a: "No. The audit is vendor-neutral and the plan is yours once it's paid for. Eva and CoBrain are our core and what we deploy most — where another tool fits better, we use it." },
  { q: "What if I already have someone technical?", a: "Even better. We hand them the plan and train them on it, and step back as soon as they've got it." },
];

export default function Services() {
  const { lane, setLane } = useServicesLane();
  const [outlineOpen, setOutlineOpen] = useState(false);
  const activeLane = LANES.find((l) => l.id === lane) || null;
  return (
    <div style={{ background: PALETTE.bg, color: PALETTE.ink, fontFamily: FONT_BODY, minHeight: "100vh" }}>
      <Helmet>
        <title>AI Services for Small Business — Audit, Build & Training | HireEva</title>
        <meta name="description" content="Not ready to manage AI yourself? Our engineers audit your business, build what pays off, train your team — or run it for you. Built for 1–30 person companies." />
        <link rel="canonical" href="https://www.hireeva.io/services" />
        <meta property="og:url" content="https://www.hireeva.io/services" />
        <meta property="og:title" content="AI Services for Small Business — Audit, Build & Training" />
        <meta property="og:description" content="Our AI ops team audits your business, builds what pays off, and trains your people — or runs it for you." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "AI implementation services for small business",
          serviceType: "AI consulting, implementation and training",
          provider: { "@type": "Organization", name: "HireEva", url: "https://www.hireeva.io" },
          areaServed: "US",
          audience: { "@type": "BusinessAudience", name: "Small businesses, 1-30 employees" },
          url: "https://www.hireeva.io/services",
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "HireEva AI services",
            itemListElement: SERVICES.map((sv) => ({
              "@type": "Offer",
              itemOffered: { "@type": "Service", name: sv.name, description: sv.body },
            })),
          },
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.hireeva.io/" },
            { "@type": "ListItem", position: 2, name: "Services", item: "https://www.hireeva.io/services" },
          ],
        })}</script>
      </Helmet>

      {/* Nav — shared site chrome */}
      <SiteNav product="services" sections={NAV_SECTIONS} callSource="services_nav" />

      {/* Hero */}
      <header style={{ position: "relative", isolation: "isolate", padding: "clamp(84px, 12vw, 132px) clamp(20px, 5vw, 24px) clamp(40px, 7vw, 72px)" }}>
        <HeroAmbient />
        <div className="svc-hero" style={{ position: "relative", zIndex: 1, maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "minmax(0,1.05fr) minmax(0,0.95fr)", gap: "clamp(28px, 5vw, 56px)", alignItems: "start" }}>
        <div>
          <div style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.muted, marginBottom: 18 }}>
            HireEva Services · by ElectricSheep
          </div>
          <h1 style={{
            fontFamily: FONT_DISPLAY, fontWeight: 400, fontSize: "clamp(38px, 7vw, 72px)",
            lineHeight: 1.05, letterSpacing: "-0.015em", margin: 0, textWrap: "balance",
          }}>
            AI operations, <em style={{ color: PALETTE.amber }}>done with you.</em>
          </h1>
          <p className="svc-hero-sub" style={{ marginTop: 24, fontSize: 18, lineHeight: 1.6, color: PALETTE.ink2, maxWidth: 620 }}>
            Your fractional AI team. An engineer maps your business, tells you where AI pays off,
            then builds it — and either trains your people to run it or runs it for you.
          </p>
          <p style={{ marginTop: 12, fontFamily: FONT_MONO, fontSize: 12, letterSpacing: "0.10em", textTransform: "uppercase", color: PALETTE.amber, fontWeight: 700 }}>
            Live in days · from $1,500/mo · scoped on the call
          </p>
          <div data-svc-hero-cta style={{ marginTop: 32, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
            <CallButton source="services_hero" />
            <button type="button" onClick={() => setOutlineOpen(true)} className="svc-hero-secondary" style={{
              display: "inline-flex", alignItems: "center", minHeight: 44, padding: "10px 18px",
              fontSize: 15, color: PALETTE.ink2, background: "transparent", cursor: "pointer",
              borderRadius: 999, border: `1px solid ${PALETTE.line}`, fontFamily: FONT_BODY,
            }}>
              Send me the one-page outline
            </button>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 14 }}>
            <HeroTicker />
            <span style={{ marginTop: 18 }}><AvailabilityPill /></span>
          </div>
        </div>
          <Reveal><AuditSample /></Reveal>
        </div>
        <div style={{ position: "relative", zIndex: 1, maxWidth: 1100, margin: "clamp(36px, 6vw, 56px) auto 0" }}>
          <Reveal><OutcomeStrip variant="services" /></Reveal>
        </div>
      </header>

      <PartnerMarquee variant="services" />

      {/* Three services */}
      <section id="what" style={{ padding: "clamp(44px, 8vw, 88px) clamp(20px, 5vw, 24px)", background: PALETTE.bgAlt, scrollMarginTop: 110, borderTop: `1px solid ${PALETTE.line}` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 400, fontSize: "clamp(28px, 4.5vw, 48px)", margin: "0 0 14px", letterSpacing: "-0.015em" }}>
            Three ways we work
          </h2>
          <p style={{ margin: "0 0 28px", fontSize: 16, color: PALETTE.ink2, maxWidth: 640, minHeight: 52 }}>
            {activeLane
              ? activeLane.sub
              : "Most businesses start with the audit and pick a lane from there. You can also skip straight to a build."}
          </p>
          <LaneChooser value={lane} onChange={setLane} />
          <div className="svc-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            {SERVICES.map((s, i) => {
              const matched = activeLane?.maps === s.n;
              const dimmed = !!activeLane && !matched;
              return (
              <Reveal key={s.n} delay={i * 70}>
              <article data-svc-card style={{
                background: PALETTE.card,
                border: `1px solid ${matched ? PALETTE.amber : PALETTE.line}`,
                boxShadow: matched ? "0 20px 48px -28px rgba(196,130,58,0.75)" : "none",
                opacity: dimmed ? 0.55 : 1,
                borderRadius: 18,
                padding: 26, display: "flex", flexDirection: "column", height: "100%",
                transition: "transform 220ms ease, box-shadow 220ms ease, opacity 220ms ease, border-color 220ms ease",
              }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14 }}>
                  <span style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: 26, color: PALETTE.amber, lineHeight: 1 }}>{s.n}</span>
                  <span style={{ fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", color: matched ? PALETTE.amber : PALETTE.muted, fontWeight: matched ? 700 : 400 }}>
                    {matched ? "Your lane" : s.tag}
                  </span>
                </div>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontWeight: 400, fontSize: 27, margin: "0 0 12px", letterSpacing: "-0.01em" }}>{s.name}</h3>
                <p style={{ margin: "0 0 18px", fontSize: 15, lineHeight: 1.55, color: PALETTE.ink2 }}>{s.body}</p>
                <ul style={{ listStyle: "none", padding: 0, margin: "0 0 24px", display: "grid", gap: 10 }}>
                  {s.bullets.map((b) => (
                    <li key={b} style={{ display: "flex", gap: 10, fontSize: 14, lineHeight: 1.5, color: PALETTE.ink2 }}>
                      <span aria-hidden style={{ color: PALETTE.amber, fontWeight: 700 }}>·</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div style={{ marginTop: "auto" }} className="svc-card-cta" data-matched={matched ? "true" : "false"}>
                  <CallButton
                    source={activeLane && matched ? `services_lane_${activeLane.id}` : `services_card_${s.n}`}
                    variant={matched ? "solid" : "ghost"}
                  />
                </div>
              </article>
              </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Payback estimator */}
      <section id="payback" style={{ padding: "clamp(44px, 8vw, 88px) clamp(20px, 5vw, 24px)", scrollMarginTop: 110 }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Reveal><PaybackEstimator /></Reveal>
          <p style={{ margin: "18px 0 0", fontSize: 14.5, color: PALETTE.ink2 }}>
            Not ready to talk?{" "}
            <button
              type="button"
              onClick={() => setOutlineOpen(true)}
              style={{ background: "none", border: 0, padding: 0, color: PALETTE.amber, fontWeight: 600, cursor: "pointer", fontSize: 14.5, fontFamily: FONT_BODY }}
            >
              Get the one-page outline by email →
            </button>
          </p>
        </div>
      </section>

      {/* Compare */}
      <section id="compare" style={{ padding: "clamp(44px, 8vw, 88px) clamp(20px, 5vw, 24px)", scrollMarginTop: 110 }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 400, fontSize: "clamp(28px, 4.5vw, 48px)", margin: "0 0 14px", letterSpacing: "-0.015em" }}>
            Us versus the alternatives
          </h2>
          <p style={{ margin: "0 0 32px", fontSize: 16, color: PALETTE.ink2, maxWidth: 640 }}>
            We build and run the software ourselves, so nothing waits on a vendor. Here's how that changes the engagement.
          </p>
          <Reveal><CompareMatrix /></Reveal>
        </div>
      </section>

      {/* How it works */}
      <section id="how" style={{ padding: "clamp(44px, 8vw, 88px) clamp(20px, 5vw, 24px)", background: PALETTE.bgAlt, scrollMarginTop: 110 }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 400, fontSize: "clamp(28px, 4.5vw, 48px)", margin: "0 0 40px", letterSpacing: "-0.015em" }}>
            How an AI implementation actually runs
          </h2>
          <Reveal><ServiceTimeline /></Reveal>
        </div>
      </section>

      {/* Verticals */}
      <section id="verticals" style={{ padding: "clamp(44px, 8vw, 88px) clamp(20px, 5vw, 24px)", scrollMarginTop: 110 }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 400, fontSize: "clamp(28px, 4.5vw, 48px)", margin: "0 0 14px", letterSpacing: "-0.015em" }}>
            Where we usually start, by industry
          </h2>
          <p style={{ margin: "0 0 32px", fontSize: 16, color: PALETTE.ink2, maxWidth: 640 }}>
            Different businesses leak in different places. Here's the first workflow we'd look at in yours.
          </p>
          <Reveal><VerticalStrip /></Reveal>
        </div>
      </section>

      {/* Fit check — absorbs the old "who it's for" section */}
      <section id="fit" style={{ padding: "clamp(44px, 8vw, 88px) clamp(20px, 5vw, 24px)", background: PALETTE.dark, color: "#fff", scrollMarginTop: 110 }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 400, fontSize: "clamp(28px, 4.5vw, 46px)", margin: "0 0 14px", letterSpacing: "-0.015em" }}>
            Built for 1 to 30 person businesses
          </h2>
          <p style={{ margin: "0 0 14px", fontSize: 17, lineHeight: 1.6, color: "rgba(255,255,255,0.78)", maxWidth: 720 }}>
            The big consultancies will take six figures and eighteen months to tell you what your own team
            already suspects. That doesn't work for a business with a dozen people and a payroll to make.
            We send one engineer, look at real work, and build the two or three things that move money.
          </p>
          <p style={{ margin: "0 0 32px", fontSize: 15.5, lineHeight: 1.6, color: "rgba(255,255,255,0.6)", maxWidth: 720 }}>
            We'd rather you work this out before the call than after an invoice. Here's the honest split.
          </p>
          <Reveal><FitCheck tone="dark" /></Reveal>
          <div style={{ marginTop: 32 }}>
            <CallButton source="services_audience" label="See if we're a fit" />
          </div>
          <Reveal>
            <div style={{
              marginTop: "clamp(32px, 5vw, 48px)",
              borderTop: "1px solid rgba(255,255,255,0.14)", paddingTop: "clamp(24px, 3.5vw, 32px)",
            }}>
              <div style={{
                fontFamily: FONT_MONO, fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase",
                color: PALETTE.amberSoft, fontWeight: 700, marginBottom: 20,
              }}>
                What you're not risking
              </div>
              <div className="svc-grid-4" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "clamp(18px, 3vw, 28px)" }}>
                {RISK.map((r) => (
                  <div key={r.t}>
                    <h3 style={{ fontFamily: FONT_DISPLAY, fontWeight: 400, fontSize: 20, margin: "0 0 8px", letterSpacing: "-0.01em", color: "#fff" }}>{r.t}</h3>
                    <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: "rgba(255,255,255,0.7)" }}>{r.b}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Proof */}
      <section id="proof" style={{ scrollMarginTop: 110, padding: "clamp(44px, 8vw, 88px) clamp(20px, 5vw, 24px)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 400, fontSize: "clamp(28px, 4.5vw, 46px)", margin: "0 0 14px", letterSpacing: "-0.015em" }}>
            What the work actually looks like
          </h2>
          <p style={{ margin: "0 0 28px", fontSize: 14, color: PALETTE.muted, maxWidth: 640, lineHeight: 1.55 }}>
            We're early, so everything below is an anonymised composite of real engagements — details
            changed, workflows and quotes unchanged. We'd rather say that than borrow a logo.
          </p>
          <Reveal><EngagementSnapshots /></Reveal>
          <h3 style={{ fontFamily: FONT_DISPLAY, fontWeight: 400, fontSize: "clamp(24px, 3.4vw, 34px)", margin: "clamp(36px, 6vw, 56px) 0 22px", letterSpacing: "-0.015em" }}>
            What owners say
          </h3>
          <Reveal><TestimonialWall variant="services" /></Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" style={{ padding: "clamp(44px, 8vw, 88px) clamp(20px, 5vw, 24px)", background: PALETTE.bgAlt, scrollMarginTop: 110 }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <div id="team" style={{ scrollMarginTop: 110, marginBottom: "clamp(40px, 6vw, 64px)" }}>
            <Reveal><EngineerCard /></Reveal>
          </div>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 400, fontSize: "clamp(28px, 4.5vw, 46px)", margin: "0 0 32px", letterSpacing: "-0.015em" }}>
            Questions
          </h2>
          <div style={{ display: "grid", gap: 0 }}>
            {FAQS.map((f) => (
              <details key={f.q} style={{ borderBottom: `1px solid ${PALETTE.line}`, padding: "18px 0" }}>
                <summary style={{ cursor: "pointer", fontSize: 17, fontWeight: 600, color: PALETTE.ink, listStyle: "none" }}>
                  {f.q}
                </summary>
                <p style={{ margin: "12px 0 0", fontSize: 15.5, lineHeight: 1.6, color: PALETTE.ink2 }}>{f.a}</p>
              </details>
            ))}
          </div>
          <div style={{ marginTop: 26, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 14 }}>
            <span style={{ fontSize: 15.5, color: PALETTE.ink2 }}>Still not sure? Ask it on the call.</span>
            <CallButton source="services_faq" variant="ghost" label="Ask an engineer" />
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section style={{ padding: "clamp(48px, 9vw, 96px) clamp(20px, 5vw, 24px)" }}>
        <div className="svc-close" style={{
          maxWidth: 1000, margin: "0 auto", display: "grid",
          gridTemplateColumns: "minmax(0,1.05fr) minmax(0,0.95fr)",
          gap: "clamp(24px, 4vw, 48px)", alignItems: "center",
        }}>
          <div>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 400, fontSize: "clamp(30px, 5vw, 54px)", margin: "0 0 18px", letterSpacing: "-0.015em" }}>
              {activeLane ? activeLane.closeTitle : "Let's find the money first."}
            </h2>
            <p style={{ margin: "0 0 28px", maxWidth: 480, fontSize: 17, lineHeight: 1.6, color: PALETTE.ink2 }}>
              {activeLane
                ? activeLane.closeSub
                : "Fifteen minutes with the person who'd build it. You'll leave knowing which parts of your operation are worth automating, and which ones aren't."}
            </p>
            <CallButton source={activeLane ? `services_lane_${activeLane.id}_footer` : "services_footer"} />
            <p style={{ margin: "16px 0 0", fontSize: 13, color: PALETTE.muted }}>
              No deck, no discovery process, no obligation.
            </p>
            <AfterClick />
          </div>
          <div style={{
            padding: "22px 22px 24px",
            background: PALETTE.card, border: `1px solid ${PALETTE.line}`, borderRadius: 18,
          }}>
            <AuditOutlineForm context={activeLane?.context} />
          </div>
        </div>
        <div style={{ marginTop: 32, fontSize: 14.5, color: PALETTE.ink2, textAlign: "center" }}>
          Rather run the software yourself?{" "}
          <Link to="/" style={{ color: PALETTE.amber, fontWeight: 600, textDecoration: "none" }}>
            See Eva pricing →
          </Link>
        </div>
      </section>

      <SiteFooter product="services" callSource="services_footer" />

      <SectionRail sections={RAIL_SECTIONS} />
      <StickyCallBar label={activeLane?.sticky} />
      <ExitIntent />
      <OutlineModal open={outlineOpen} onClose={() => setOutlineOpen(false)} context={activeLane?.context} />

      <style>{`
        [data-reveal] { opacity: 0; transform: translateY(14px); transition: opacity 600ms ease, transform 600ms ease; }
        [data-reveal][data-revealed="true"] { opacity: 1; transform: none; }
        @media (prefers-reduced-motion: reduce) { [data-reveal] { opacity: 1; transform: none; transition: none; } }
        [data-svc-card]:hover { transform: translateY(-3px); box-shadow: 0 18px 44px -26px rgba(0,0,0,0.4); }
        @media (max-width: 700px) { .svc-nav-link { display: none !important; } .svc-nav-right { gap: 10px !important; } nav [data-svc-cta] { padding: 12px 16px !important; font-size: 14px !important; white-space: nowrap; } }
        @media (max-width: 980px) { .svc-hero { grid-template-columns: 1fr !important; } .svc-grid-4 { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 980px) { .svc-est-grid { grid-template-columns: 1fr !important; } .svc-close { grid-template-columns: 1fr !important; } }
        @media (max-width: 900px) { .svc-fit-grid { grid-template-columns: 1fr !important; } }
        @media (max-width: 560px) { .svc-grid-4 { grid-template-columns: 1fr !important; } .svc-outline-grid { grid-template-columns: 1fr !important; } }
        @media (max-width: 760px) {
          .svc-sticky-bar [data-svc-cta] { padding: 12px 18px !important; white-space: nowrap; }
          .svc-card-cta[data-matched="false"] { display: none !important; }
          .svc-hero-sub { font-size: 16.5px !important; margin-top: 18px !important; }
          .svc-hero-secondary { width: 100%; justify-content: center; }
          .svc-lane-row button { flex: 1 1 100% !important; }
          .svc-grid { align-items: start !important; }
        }
        @media (hover: none) { [data-svc-card]:hover { transform: none !important; box-shadow: none !important; } }
        [data-svc-cta="solid"]:hover { background: #b3742f; transform: translateY(-1px); }
        [data-svc-cta="ghost"]:hover { background: rgba(0,0,0,0.04); }
        details > summary::-webkit-details-marker { display: none; }
        @media (max-width: 900px) { .svc-grid { grid-template-columns: 1fr !important; } }
        @media (hover: none) { [data-svc-cta]:hover { transform: none !important; } }
      `}</style>
    </div>
  );
}
